import { Heading, IconButton } from '@assembly-js/design-system';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const EMPTY_PROGRESS_WIDTH_PX = 9;

interface ChecklistWrapperProps {
  /** Whole-number completion percentage (0-100) driving the progress bar. */
  completionPct: number;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  children: ReactNode;
  /** When true the card title switches to the "You're all set" completion copy. */
  isComplete?: boolean;
  isClient?: boolean;
  backgroundColor?: string;
  sidebarColor?: string;
  textColor?: string;
}

export const ChecklistWrapper = ({
  completionPct,
  isCollapsed,
  onToggleCollapse,
  children,
  isComplete,
  isClient,
  backgroundColor,
  sidebarColor,
  textColor,
}: ChecklistWrapperProps) => {
  const { t } = useTranslation();

  const title = isComplete
    ? t(($) => $.page_apps.app_builder.getting_started.all_set_title)
    : t(($) => $.page_apps.app_builder.getting_started.title);

  const progressWidth =
    completionPct === 0 ? `${EMPTY_PROGRESS_WIDTH_PX}px` : `${completionPct}%`;

  return (
    <div
      className={clsx(
        'box-border mb-1 w-full max-w-full overflow-hidden rounded-lg border border-solid',
        { 'border-gray-150 bg-white': !isClient },
      )}
      style={
        isClient ? { backgroundColor, borderColor: backgroundColor } : undefined
      }
    >
      <div className="box-border flex w-full max-w-full flex-col gap-1 overflow-hidden px-2 py-1">
        <div
          className="box-border flex w-full items-center justify-between"
          style={isClient ? { color: textColor } : undefined}
        >
          <Heading
            size="xs"
            className={clsx('!m-0 min-w-0 flex-1 !leading-5', {
              'text-black-heading': !isClient,
            })}
          >
            {title}
          </Heading>
          <IconButton
            icon={isCollapsed ? 'angle-right' : 'angle-down'}
            variant="minimal"
            aria-expanded={!isCollapsed}
            aria-label={
              isCollapsed
                ? t(
                    ($) =>
                      $.page_apps.app_builder.getting_started.expand_checklist,
                  )
                : t(
                    ($) =>
                      $.page_apps.app_builder.getting_started
                        .collapse_checklist,
                  )
            }
            onClick={onToggleCollapse}
            className={clsx('shrink-0 h-5 bg-transparent border-0', {
              'text-black-heading': !isClient,
              'w-4': !isCollapsed,
              'w-[6px]': isCollapsed,
            })}
            style={isClient ? { color: textColor } : undefined}
          />
        </div>
        <div
          role="progressbar"
          aria-label={title}
          aria-valuenow={completionPct}
          aria-valuemin={0}
          aria-valuemax={100}
          className={clsx(
            'box-border h-[3px] w-full max-w-full overflow-hidden rounded',
            { 'bg-gray-150': !isClient },
          )}
          style={isClient ? { backgroundColor: sidebarColor } : undefined}
        >
          <div
            className={clsx('h-[3px] rounded transition-[width]', {
              'bg-black-heading': !isClient,
            })}
            style={
              isClient
                ? { width: progressWidth, backgroundColor: textColor }
                : { width: progressWidth }
            }
          />
        </div>
      </div>
      {!isCollapsed && children}
    </div>
  );
};
